/**
 * SupplierPaymentsPage — COM-05 / COM-AP: Dos tabs:
 *   Facturas → lista de facturas de proveedor con estado de pago, detalle y registro de pago
 *   Pagos    → registro y confirmación de pagos a proveedores
 */
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { fmtDate } from '@/lib/utils'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import {
  Banknote, FileText, Plus, Loader2, CheckCircle, XCircle, AlertCircle, Eye,
} from 'lucide-react'
import { useFilters, FilterBar, type FilterConfig } from '@/components/filters'
import { KebabMenu } from '@/components/KebabMenu'
import {
  sortStateToParam, loadSortFromStorage, saveSortToStorage,
  type SortState,
} from '@/components/SortableHeader'
import { DataTable } from '@/components/ds/DataTable'
import { ordenControlado } from '@/components/ds/ordenControlado'
import { usePermission } from '@/hooks/usePermission'
import { useToast } from '@/components/Toast'
import {
  useSupplierPayments, useCreateSupplierPayment,
  useConfirmSupplierPayment, useCancelSupplierPayment,
  usePayableInvoices, useSupplierInvoice, useApAgingSummary,
  type SupplierPaymentListItem, type SupplierPaymentStatus,
  type PayableInvoice, type PayableInvoiceStatus, type ApAgingSummaryResponse,
} from '../hooks/usePurchases'
import { SupplierPaymentModal } from './SupplierPaymentModal'
import { SupplierPaymentDetailModal } from './SupplierPaymentDetailModal'
import { SupplierInvoiceDetailModal } from './SupplierInvoiceDetailModal'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { PAYABLE_STATUS } from '../../../components/ds/statusCatalogs'
import { Pagination } from '@/components/ds/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { AGING_BUCKETS } from '@/components/ds/agingBuckets'

// ─── Tipos y configuraciones ─────────────────────────────────────────────────

type ActiveTab = 'invoices' | 'payments'


const PAY_STATUS_CONFIG: Record<SupplierPaymentStatus, { label: string; cls: string; dotCls: string }> = {
  registered: { label: 'Registrado',  cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
  confirmed:  { label: 'Confirmado',  cls: 'bg-success-surface text-success-content', dotCls: 'bg-emerald-400' },
  cancelled:  { label: 'Anulado',     cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
}

const METHOD_LABELS: Record<string, string> = {
  transfer: 'Transferencia',
  check: 'Cheque',
  cash: 'Efectivo',
  other: 'Otro',
}

// ─── Badges ──────────────────────────────────────────────────────────────────

function PayStatusBadge({ status, isPartial }: { status: PayableInvoiceStatus; isPartial?: boolean }) {
  // Vencida con abonos (#1486): sale en «Pago Parcial» y en «Vencido», así que la
  // pastilla nombra las dos cosas. El tono sigue siendo el de vencido.
  if (status === 'overdue' && isPartial) {
    return <StatusBadge status={status} catalog={PAYABLE_STATUS} label="Vencido · parcial" />
  }
  return <StatusBadge status={status} catalog={PAYABLE_STATUS} />
}

function PaymentStatusBadge({ status }: { status: SupplierPaymentStatus }) {
  const cfg = PAY_STATUS_CONFIG[status] ?? PAY_STATUS_CONFIG.registered
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

function DueDateCell({ due, status }: { due: string | null; status: PayableInvoiceStatus }) {
  if (!due) return <span className="text-content-subtle">—</span>
  const isOverdue = status === 'overdue'
  return (
    <span className={`text-sm ${isOverdue ? 'text-danger-content-soft font-medium' : 'text-content-muted'}`}>
      {due}
      {isOverdue && <AlertCircle size={12} className="inline ml-1 text-danger-solid" />}
    </span>
  )
}

// ─── Tarjetas de antigüedad navegables (homologado con Cobranza/CxC) ───────────

function ApAgingSummary({
  aging, isLoading, activeBucket, onBucketClick,
}: {
  aging?: ApAgingSummaryResponse
  isLoading: boolean
  activeBucket: string | null
  onBucketClick: (key: string) => void
}) {
  const fmt = (v: string) =>
    formatMoneyForTenant(v)
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-surface-muted rounded-xl animate-pulse" />)}
      </div>
    )
  }
  const buckets = AGING_BUCKETS.map(b => ({ key: b.key, label: b.label, color: b.card }))
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {buckets.map(({ key, label, color }) => {
        const bucket = aging?.buckets?.[key]
        const isActive = activeBucket === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onBucketClick(key)}
            aria-pressed={isActive}
            title={isActive ? 'Quitar filtro' : `Filtrar facturas: ${label}`}
            className={`text-left rounded-xl border p-3 transition-all cursor-pointer hover:brightness-105 hover:shadow-sm focus:outline-none ${color} ${
              isActive ? 'ring-2 ring-offset-1 ring-offset-canvas ring-current shadow-sm' : 'opacity-95'
            }`}
          >
            <p className="text-xs font-medium opacity-80">{label}</p>
            <p className="text-lg font-bold mt-0.5">{bucket?.count ?? 0}</p>
            <p className="text-xs opacity-70 truncate">{bucket?.total ? fmt(bucket.total) : '—'}</p>
          </button>
        )
      })}
    </div>
  )
}

// ─── Page principal ───────────────────────────────────────────────────────────

export function SupplierPaymentsPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('invoices')

  // Estado del modal de pago (compartido entre ambas tabs)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [paymentForInvoice, setPaymentForInvoice] = useState<PayableInvoice | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const { can } = usePermission()
  const toast = useToast()
  const canCreate = can('purchases.payments', 'create')

  const createPayment = useCreateSupplierPayment()

  const handleOpenPaymentModal = (invoice?: PayableInvoice) => {
    setPaymentForInvoice(invoice ?? null)
    setApiError(null)
    setPaymentModalOpen(true)
  }

  const handleSavePayment = (formData: Record<string, unknown>) => {
    setApiError(null)
    createPayment.mutate(formData as Parameters<typeof createPayment.mutate>[0], {
      onSuccess: (data) => {
        setPaymentModalOpen(false)
        setPaymentForInvoice(null)
        if (data?.voucher_draft_warning) {
          toast.warning('Pago registrado. El comprobante quedó en borrador: revisar la cuenta contable (documento con monto no facturable).')
        }
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al registrar')
      },
    })
  }

  return (
    <div className="space-y-5">
      {/* Botón global Registrar Pago */}
      {canCreate && (
        <div className="flex justify-end">
          <button
            onClick={() => handleOpenPaymentModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={15} />Registrar Pago
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-divider-strong">
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px
            ${activeTab === 'invoices'
              ? 'border-primary text-primary'
              : 'border-transparent text-content-muted hover:text-content'}`}>
          <span className="flex items-center gap-1.5"><FileText size={14} />Facturas</span>
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px
            ${activeTab === 'payments'
              ? 'border-primary text-primary'
              : 'border-transparent text-content-muted hover:text-content'}`}>
          <span className="flex items-center gap-1.5"><Banknote size={14} />Pagos</span>
        </button>
      </div>

      {activeTab === 'invoices'
        ? <InvoicesTab onRegisterPayment={handleOpenPaymentModal} />
        : <PaymentsTab />}

      {/* Modal pago */}
      {paymentModalOpen && (
        <SupplierPaymentModal
          preselectedInvoice={paymentForInvoice ?? undefined}
          onSave={handleSavePayment}
          onClose={() => { setPaymentModalOpen(false); setPaymentForInvoice(null) }}
          isPending={createPayment.isPending}
          error={apiError}
        />
      )}
    </div>
  )
}

// ─── Facturas Tab ─────────────────────────────────────────────────────────────

function InvoicesTab({ onRegisterPayment }: { onRegisterPayment: (inv: PayableInvoice) => void }) {
  const { can } = usePermission()
  const canCreatePay = can('purchases.payments', 'create')
  const locale = useTenantLocale()
  const [statusFilter, setStatusFilter] = useState('')
  const [agingBucket, setAgingBucket] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [detailId, setDetailId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { data: locConfig } = useLocalizationConfig()
  // Con la Vista 360 de compras activa, abrir una factura ES el 360; si no, el modal.
  const openInvoice = (invId: string) => {
    if (locConfig?.ui_purchases_360) navigate(`/purchases/invoices/${invId}/360`)
    else setDetailId(invId)
  }
  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const, grow: true,
      placeholder: 'Buscar por N° factura, proveedor, RUT o monto (> 100000, < 50000)…' },
    { key: 'date', type: 'date-range' as const, label: 'Emisión', placement: 'quick' as const, presets: true },
  ], [])
  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1), persistKey: 'payables-invoices' })
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('payables-invoices-sort', { field: 'created_at', direction: 'desc' }),
  )
  const handleSortChange = (next: SortState) => {
    setSort(next)
    saveSortToStorage('payables-invoices-sort', next)
    setPage(1)
  }

  const { data: aging, isLoading: agingLoading } = useApAgingSummary()

  // El tramo de aging y el estado son filtros mutuamente excluyentes (como en CxC).
  const handleBucketClick = (key: string) => {
    setAgingBucket(prev => (prev === key ? null : key))
    setStatusFilter('')
    setPage(1)
  }
  const handleStatusClick = (s: string) => {
    setStatusFilter(s)
    setAgingBucket(null)
    setPage(1)
  }

  const { data, isLoading } = usePayableInvoices({
    payment_status: statusFilter || undefined,
    aging_bucket: agingBucket || undefined,
    ...filters.params,
    ordering: sortStateToParam(sort),
    page,
    page_size: 25,
  })

  // Carga la factura completa cuando el usuario abre el detalle
  const { data: detailInvoice, isLoading: detailLoading } = useSupplierInvoice(detailId)

  const fmtAmount = (v: string) =>
    formatMoneyForTenant(v)

  // Monto en la moneda original (facturas intl). Ej.: "USD 5.037,00".
  const fmtFx = (v: string | null | undefined, cur: string | null | undefined) => {
    if (!v || !cur) return null
    return `${cur} ${parseFloat(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  return (
    <div className="space-y-4">
      {/* Tarjetas de antigüedad navegables (homologado con Cobranza) */}
      <ApAgingSummary
        aging={aging}
        isLoading={agingLoading}
        activeBucket={agingBucket}
        onBucketClick={handleBucketClick}
      />

      <div className="flex items-center justify-between">
        <span className="text-sm text-content-muted">{data?.count ?? 0} factura{data?.count !== 1 ? 's' : ''}</span>
      </div>

      {/* Buscador inteligente (N° factura / proveedor / monto) + rango de fecha */}
      <FilterBar config={filterConfig} filters={filters} />

      {/* Filtro por estado de pago */}
      <div className="flex gap-2 flex-wrap">
        {(['', 'pending', 'partial', 'paid', 'overdue'] as const).map(s => (
          <button
            key={s}
            onClick={() => handleStatusClick(s)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${statusFilter === s
                ? 'bg-primary text-on-primary border-primary'
                : 'border-divider text-content-muted hover:bg-soft'}`}>
            {s ? (PAYABLE_STATUS[s]?.label ?? s) : 'Todas'}
          </button>
        ))}
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : !data?.invoices.length ? (
          <EmptyState
            icon={FileText}
            title="No hay facturas registradas."
            hint={statusFilter ? 'No hay facturas con este estado.' : 'Las facturas de proveedores aprobadas aparecerán aquí.'}
            size="md"
          />
        ) : (
          <DataTable
            columns={[
              // 🔴 La `key` es el nombre del campo que el backend ordena
              // (`?ordering=`), no una etiqueta: es lo que viaja.
              {
                key: 'supplier', header: 'Proveedor', sortable: true,
                render: (inv: PayableInvoice) => (
                  <>
                    <div className="text-sm font-medium text-content">{inv.supplier_name}</div>
                    <div className="text-xs text-content-subtle">{inv.supplier_tax_id}</div>
                  </>
                ),
              },
              { key: 'invoice_number', header: 'N° Factura', sortable: true, render: (inv: PayableInvoice) => inv.invoice_number },
              {
                key: 'issue_date', header: 'Emisión', sortable: true,
                render: (inv: PayableInvoice) => (inv.issue_date ? fmtDate(inv.issue_date, locale) : '—'),
              },
              {
                key: 'due_date', header: 'Vencimiento', sortable: true,
                render: (inv: PayableInvoice) => <DueDateCell due={inv.due_date} status={inv.payment_status} />,
              },
              {
                key: 'payment_status', header: 'Estado Pago', sortable: true,
                render: (inv: PayableInvoice) => <PayStatusBadge status={inv.payment_status} isPartial={inv.is_partial} />,
              },
              {
                key: 'total', header: 'Total', sortable: true, numeric: true,
                render: (inv: PayableInvoice) => (
                  <>
                    <div className="text-sm font-medium text-content">{fmtAmount(inv.total)}</div>
                    {inv.is_non_billable && (
                      <div className="text-[10px] text-warning-content-soft" title="Monto no facturable: la deuda a pagar figura en el Saldo">No facturable</div>
                    )}
                    {fmtFx(inv.total_original, inv.currency_original) && (
                      <div className="text-[11px] text-content-subtle tabular-nums">{fmtFx(inv.total_original, inv.currency_original)}</div>
                    )}
                  </>
                ),
              },
              {
                key: 'paid_amount', header: 'Pagado', numeric: true,
                render: (inv: PayableInvoice) => (
                  <>
                    <div className="text-sm text-success-content-soft">{fmtAmount(inv.paid_amount)}</div>
                    {fmtFx(inv.paid_original, inv.currency_original) && (
                      <div className="text-[11px] text-content-subtle tabular-nums">{fmtFx(inv.paid_original, inv.currency_original)}</div>
                    )}
                  </>
                ),
              },
              {
                key: 'outstanding', header: 'Saldo', numeric: true,
                render: (inv: PayableInvoice) => (
                  <>
                    <div className={`text-sm font-semibold ${parseFloat(inv.outstanding) > 0 ? 'text-content' : 'text-success-content-soft'}`}>
                      {fmtAmount(inv.outstanding)}
                    </div>
                    {fmtFx(inv.outstanding_original, inv.currency_original) && (
                      <div className="text-[11px] text-content-subtle tabular-nums">{fmtFx(inv.outstanding_original, inv.currency_original)}</div>
                    )}
                  </>
                ),
              },
              {
                key: 'acciones', header: 'Acción',
                render: (inv: PayableInvoice) => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => openInvoice(inv.id)}
                      title="Ver detalle"
                      className="p-1.5 rounded-lg text-content-subtle hover:text-content-muted hover:bg-soft transition-colors">
                      <Eye size={14} />
                    </button>
                    {parseFloat(inv.outstanding) > 0 && canCreatePay && (
                      <button
                        onClick={() => onRegisterPayment(inv)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-primary border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
                        <Plus size={11} />Pago
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={data.invoices}
            rowKey={(inv: PayableInvoice) => inv.id}
            onRowClick={(inv: PayableInvoice) => openInvoice(inv.id)}
            // Orden por SERVIDOR: sin estas props el componente ordenaría solo
            // la página visible y quien mira creería que ordenó todo.
            {...ordenControlado(sort, handleSortChange)}
            surface={false}
            minWidth={1100}
            caption="Facturas de proveedor por pagar"
          />
        )}
      </div>

      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {/* Modal de detalle de factura */}
      <SupplierInvoiceDetailModal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        invoice={detailLoading ? null : (detailInvoice ?? null)}
      />
    </div>
  )
}

// ─── Pagos Tab ────────────────────────────────────────────────────────────────

function PaymentsTab() {
  const { can } = usePermission()
  const canApprovePay = can('purchases.payments', 'approve')
  const canDeletePay = can('purchases.payments', 'delete')
  const locale = useTenantLocale()
  const [statusFilter, setStatusFilter] = useState('')
  const [acctFilter, setAcctFilter] = useState('')
  const [page, setPage] = useState(1)
  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const, grow: true,
      placeholder: 'Buscar por N° factura, proveedor, referencia o monto (> 100000)…' },
    { key: 'date', type: 'date-range' as const, label: 'Fecha de pago', placement: 'quick' as const, presets: true },
  ], [])
  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1), persistKey: 'supplier-payments' })
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('supplier-payments-sort', { field: 'created_at', direction: 'desc' }),
  )
  const handleSortChange = (next: SortState) => {
    setSort(next)
    saveSortToStorage('supplier-payments-sort', next)
    setPage(1)
  }
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)

  const { data, isLoading } = useSupplierPayments({
    status: statusFilter || undefined,
    accounting_status: acctFilter || undefined,
    ...filters.params,
    ordering: sortStateToParam(sort),
    page,
    page_size: 25,
  })

  const confirm = useConfirmSupplierPayment()
  const cancelPayment = useCancelSupplierPayment()

  const fmtAmount = (v: string) =>
    formatMoneyForTenant(v)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-content-muted">{data?.count ?? 0} pago{data?.count !== 1 ? 's' : ''}</span>
      </div>

      {/* Buscador inteligente (N° factura / proveedor / referencia / monto) + rango de fecha */}
      <FilterBar config={filterConfig} filters={filters} />

      <div className="flex gap-2 flex-wrap">
        {(['', 'registered', 'confirmed', 'cancelled'] as const).map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1) }}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${statusFilter === s ? 'bg-primary text-on-primary border-primary' : 'border-divider text-content-muted hover:bg-soft'}`}>
            {s ? (PAY_STATUS_CONFIG[s as SupplierPaymentStatus]?.label ?? s) : 'Todos'}
          </button>
        ))}
        <span className="text-content-subtle mx-1">|</span>
        {[
          { value: '', label: 'Todos' },
          { value: 'pending', label: 'Sin contabilizar' },
          { value: 'accounted', label: 'Contabilizados' },
        ].map(opt => (
          <button key={opt.value} onClick={() => { setAcctFilter(opt.value); setPage(1) }}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${acctFilter === opt.value ? 'bg-warning-fill text-white border-warning-fill' : 'border-divider text-content-muted hover:bg-soft'}`}>
            {opt.label}
          </button>
        ))}
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : !data?.payments.length ? (
          <EmptyState icon={Banknote} title="No hay pagos registrados." size="md" />
        ) : (
          <DataTable
            columns={[
              { key: 'supplier', header: 'Proveedor', sortable: true, render: (p: SupplierPaymentListItem) => <span className="font-medium text-content">{p.supplier_name}</span> },
              { key: 'payment_date', header: 'Fecha', sortable: true, render: (p: SupplierPaymentListItem) => fmtDate(p.payment_date, locale) },
              {
                key: 'payment_method', header: 'Método', sortable: true,
                render: (p: SupplierPaymentListItem) => METHOD_LABELS[p.payment_method] ?? p.payment_method,
              },
              { key: 'reference', header: 'Referencia', sortable: true, render: (p: SupplierPaymentListItem) => p.reference || '—' },
              {
                key: 'status', header: 'Estado', sortable: true,
                render: (p: SupplierPaymentListItem) => (
                  <div className="flex flex-col gap-1">
                    <PaymentStatusBadge status={p.status} />
                    {p.status === 'confirmed' && p.accounting_status === 'pending' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning-solid" />
                        Sin contabilizar
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: 'amount', header: 'Monto', sortable: true, numeric: true,
                render: (p: SupplierPaymentListItem) => <span className="font-medium text-content">{fmtAmount(p.amount)}</span>,
              },
              {
                key: 'acciones', header: 'Acciones',
                render: (p: SupplierPaymentListItem) => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {p.status === 'registered' && canApprovePay && (
                      <button onClick={() => confirm.mutate(p.id)} disabled={confirm.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60">
                        <CheckCircle size={11} />Confirmar
                      </button>
                    )}
                    <KebabMenu items={[
                      { label: 'Anular', icon: <XCircle size={14} />, onClick: () => cancelPayment.mutate(p.id), hidden: !['registered', 'confirmed'].includes(p.status) || !canDeletePay, className: 'text-danger-content-soft hover:bg-danger-surface', disabled: cancelPayment.isPending },
                    ]} />
                  </div>
                ),
              },
            ]}
            rows={data.payments}
            rowKey={(p: SupplierPaymentListItem) => p.id}
            onRowClick={(p: SupplierPaymentListItem) => setSelectedPaymentId(p.id)}
            {...ordenControlado(sort, handleSortChange)}
            surface={false}
            minWidth={980}
            caption="Pagos a proveedores"
          />
        )}
      </div>

      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {selectedPaymentId && (
        <SupplierPaymentDetailModal
          paymentId={selectedPaymentId}
          onClose={() => setSelectedPaymentId(null)}
        />
      )}
    </div>
  )
}
